import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Star, Gift } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_northern-lights-glow/artifacts/m9am00yr_6FCF8E66-33C7-4E29-A59C-D19380935074.PNG";

const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(res => setTestimonials(res.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO 
        title="Home"
        description="Glamour Body - Premium beauty salon in Katy, Texas offering advanced facials, body contouring, waxing services, and VIP packages. Licensed Esthetician Kandi provides personalized skincare treatments. Book your appointment today!"
        keywords="beauty salon Katy Texas, body contouring Houston, facials Katy TX, skincare treatments, body sculpting, licensed esthetician, Glamour Body"
        canonical="https://glamourbodyhouston.com/"
      />
      
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 aurora-bg" />
        <div className="orb-green top-10 left-10" />
        <div className="orb-cyan top-20 -left-40" />
        <div className="orb-teal top-40 right-20" />
        <div className="orb-purple bottom-20 -right-40" />
        <div className="orb-cyan bottom-40 left-1/4" />
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto pt-24">
          <motion.img 
            src={LOGO_URL} 
            alt="Glamour Body - Premium Beauty Salon in Katy Texas" 
            className="h-40 md:h-56 mx-auto mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            data-testid="hero-logo"
          />
          
          <motion.h1 
            className="font-italiana text-4xl sm:text-5xl lg:text-6xl text-white mb-4 tracking-wider uppercase"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            data-testid="hero-title"
          >
            Advanced Skin • Sculpt • Smooth
          </motion.h1>
          
          <motion.p 
            className="text-base lg:text-lg text-white/70 mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            data-testid="hero-subtitle"
          >
            Experience the ultimate in skincare, body contouring, and beauty treatments in Katy, Texas. Where confidence begins.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link to="/services">
              <Button className="btn-primary rounded-full px-8 py-6 text-lg" data-testid="hero-services-btn">
                Explore Services
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="btn-secondary rounded-full px-8 py-6 text-lg" data-testid="hero-book-btn">
                Book Appointment
              </Button>
            </Link>
          </motion.div>
          
          <motion.div 
            className="flex items-center justify-center gap-2 mt-8 text-white/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <MapPin size={16} />
            <span className="text-sm">Katy, Texas • Greater Houston Area</span>
          </motion.div>
        </div>
      </section>
      
      {/* Featured Services Preview */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h2 
            className="font-italiana text-3xl sm:text-4xl text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Premium Beauty Services
          </motion.h2>
          <motion.p 
            className="text-white/60 mb-12 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            From advanced facials to body contouring, discover treatments tailored to enhance your natural beauty
          </motion.p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Facial Treatments", desc: "Customized facials from $125", icon: "✨" },
              { title: "Body Contouring", desc: "Sculpt & smooth from $110", icon: "💎" },
              { title: "Waxing Services", desc: "Professional waxing from $15", icon: "🌿" },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                className="glass-card rounded-2xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <span className="text-4xl mb-4 block">{item.icon}</span>
                <h3 className="font-italiana text-xl text-white mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm mb-4">{item.desc}</p>
                <Link to="/services" className="text-cyan-400 hover:text-cyan-300 text-sm">
                  View All →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-24 px-4 relative" data-testid="testimonials-section">
          <div className="orb-purple top-10 -right-60" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-italiana text-3xl sm:text-4xl text-white mb-4">What Our Clients Say</h2>
              <p className="text-white/60 max-w-2xl mx-auto">Real results, real confidence</p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.slice(0, 6).map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  className="glass-card rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  data-testid={`testimonial-card-${index}`}
                >
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-white/70 text-sm mb-4 leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t border-white/10 pt-3">
                    <p className="text-white font-medium text-sm">{testimonial.name}</p>
                    <p className="text-cyan-400/70 text-xs">{testimonial.service}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gift Card CTA */}
      <section className="py-20 px-4 relative">
        <div className="orb-teal bottom-10 left-20" />
        <div className="max-w-3xl mx-auto relative z-10">
          <motion.div
            className="glass-card rounded-2xl p-8 md:p-12 text-center border border-purple-500/20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Gift className="mx-auto mb-4 text-purple-400" size={36} />
            <h2 className="font-italiana text-3xl text-white mb-3">Give the Gift of Beauty</h2>
            <p className="text-white/60 mb-6 max-w-lg mx-auto text-sm">Know someone who deserves a little pampering? Send them a Glamour Body gift card, redeemable on any service.</p>
            <Link to="/gift-cards">
              <Button className="btn-primary rounded-full px-8 py-5" data-testid="gift-card-cta-btn">
                Shop Gift Cards <Gift className="ml-2" size={16} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
